import React, {Component} from 'react'
import confirmDialog from "../../../components/dialog/ConfirmDialog";
import ValidationUtil from "../../../components/form/validator/ValidationUtil";
import RemotingService from "../../../services/remoting-service/RemotingService";
import {TextInput, Wizard} from "../../../components";
import FormField from "../../../components/form/FormField";
import SignatureInput from "../../../components/signature-input/SignatureInput";
import DateUtil from "../../../utils/DateUtil";
import TeleconsultationFormTextEnum from "../teleconsultation/enum/TeleconsultationFormTextEnum";
import FormLanguages from "../enum/FormLanguages";
import moment from "moment";
import {isMinor} from "../../patient/Patient";
import SignatureDisplay from '../../../components/signature-input/SignatureDisplay';
import {YesNoRadioInput} from "./YesNoRadioInput";
import ClinicLogo from "../../../layout/ClinicLogo";

export default class PublicTeleconsultationConsentForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            educationalPurposesAnonymously: null,
            educationalPurposesDisclosingName: null,
            socialMediaPurposesAnonymously: null,
            socialMediaPurposesDisclosingName: null,
            participateInTeleconsultation: null
        };

        this.formFields = [];
    }

    componentDidMount() {
        const { patientId } = this.props;

        RemotingService.getRemoteCall('api/appointment/first-provider/KUWAIT/' + patientId, {},
            (firstProvider) => {
                if (firstProvider) {
                    this.fieldChanged('providerName', firstProvider.name);
                    this.fieldChanged('providerSignature', firstProvider.signature);
                }
            });
    }

    createHeader = (form) => {
        const { language } = this.props;
        const selectedLanguage = language || FormLanguages.ENGLISH.name;
        const textEnum = TeleconsultationFormTextEnum;

        return <div style={{ padding: "0px 20px 0px 20px" }}>
            <div className="row text-left">
                <div className="col-12 no-padding">
                    <ClinicLogo/>
                </div>
            </div>
            <div className="row">
                <div className="col-12 text-center no-padding" style={{ fontSize: 20, fontWeight: "bold" }}>
                    {textEnum.Teleconsultation_Consent_Form_Title[selectedLanguage]}
                </div>
            </div>
            <div className="row" style={{ fontSize: 11 }}>
                <div className="col-4 text-left no-padding">
                    <b>{textEnum.Patient_Name[selectedLanguage]}: </b> {form.patientFullName}
                </div>
                <div className="col-4 text-center no-padding">
                    <b>{textEnum.MRN[selectedLanguage]}: </b> {form.patientMrn}
                </div>
                <div className="col-4 text-right no-padding">
                    <b>{textEnum.DOB[selectedLanguage]}: </b> {DateUtil.formatDate(form.patientDOB)}
                </div>
            </div>
        </div>
    };

    fieldChanged = (fieldName, value) => {
        this.props.form[fieldName] = value;
        this.forceUpdate();
    };

    close = () => {
        confirmDialog(
            'Confirmation',
            'Are you sure you want to close this form? All input will be lost.',
            this.props.close);
    };

    submitForm = () => {
        if (ValidationUtil.checkWithNotification(this.formFields)) {
            const { consentFormCode: code, form, language } = this.props;

            RemotingService.postRemoteCall(`api/public/consent-form/code/${code}/teleconsultation-consent-form`, form);
            this.props.onFormSubmit(form);
        }
    };

    /**
     * Get patient name safely handling null or undefined values
     * @param {Object} form - Form object containing patient information
     * @returns {string} - Patient name or empty string if not available
     */
    getPatientName = (form) => {
        return form && form.patientFullName ? form.patientFullName : '';
    }

    getPatientDOB = (form) => {
        return form && form.patientDOB ? form.patientDOB : null;
    }

    /**
     * Get localized text based on the current language
     * @param {string} key - The key to look up in the text enum
     * @returns {string} - The localized text
     */
    getLocalizedText = (key) => {
        const { language } = this.props;
        const selectedLanguage = language || FormLanguages.ENGLISH.name;
        return TeleconsultationFormTextEnum[key] ? TeleconsultationFormTextEnum[key][selectedLanguage] : '';
    }

    render() {
        const form = this.props.form;

        const selectedLanguage = this.props.language || FormLanguages.ENGLISH.name;
        const enumLabel = selectedLanguage === FormLanguages.ARABIC.name ? "nameInArabic" : "name";

        const patientName = this.getPatientName(form);
        const isPatientMinor = isMinor(this.getPatientDOB(form), 'KUWAIT');

        return (
            <Wizard
                show={true} fullScreen
                footerStyle={{ backgroundColor: '#f5f5f5' }}
                buttonStyle={{ backgroundColor: '#84BE56', border: 'none' }}
                header={this.createHeader(form)}
                close={this.close}
                onSubmit={this.submitForm}>

                <Wizard.Page
                    onNext={(event) => ValidationUtil.stopEventPropagationIfValidationFails(event, this.formFields)}>
                    <div style={{ margin: "20px 20px 20px 20px" }}>
                        <p>
                            {this.getLocalizedText('Treatment_Consideration')}
                        </p>
                    </div>
                </Wizard.Page>

                <Wizard.Page
                    onNext={(event) => ValidationUtil.stopEventPropagationIfValidationFails(event, this.formFields)}
                    onPrevious={() => this.formFields = []}>
                    <div style={{ margin: "20px 20px 20px 20px" }}>
                        <p><b>{this.getLocalizedText('Electronic_Systems')}</b><br />
                            {this.getLocalizedText('Electronic_Systems_Description')}
                        </p>

                        <p><b>{this.getLocalizedText('Nature_Of_Teleconsultation')}</b><br />
                            {this.getLocalizedText('Nature_Of_Teleconsultation_Description')}
                            <ul className={"list-unstyled"}>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>a.</span>
                                        {this.getLocalizedText('Nature_Of_Teleconsultation_Point1')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>b.</span>
                                        {this.getLocalizedText('Nature_Of_Teleconsultation_Point2')}
                                    </p>
                                </li>
                            </ul>
                        </p>

                        <p><b>{this.getLocalizedText('Medical_Information_And_Records')}</b>
                            {this.getLocalizedText('Medical_Information_Description')}
                        </p>

                        <p><b>{this.getLocalizedText('Confidentiality')}</b>
                            {this.getLocalizedText('Confidentiality_Description')}
                        </p>

                        <p><b>{this.getLocalizedText('Rights')}</b>
                            {this.getLocalizedText('Rights_Description')}
                        </p>

                        <p><b>{this.getLocalizedText('Expected_Benefits')}</b><br />
                            <ul className={"list-unstyled"}>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>a.</span>
                                        {this.getLocalizedText('Expected_Benefits_Point1')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>b.</span>
                                        {this.getLocalizedText('Expected_Benefits_Point2')}
                                    </p>
                                </li>
                            </ul>
                        </p>
                        <p><b>{this.getLocalizedText('Possible_Risks')}</b><br />
                            {this.getLocalizedText('Possible_Risks_Description')}
                            <ul className={"list-unstyled"}>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>a.</span>
                                        {this.getLocalizedText('Possible_Risks_Point1')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>b.</span>
                                        {this.getLocalizedText('Possible_Risks_Point2')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>c.</span>
                                        {this.getLocalizedText('Possible_Risks_Point3')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>d.</span>
                                        {this.getLocalizedText('Possible_Risks_Point4')}
                                    </p>
                                </li>
                            </ul>
                        </p>
                    </div>
                </Wizard.Page>

                <Wizard.Page
                    onNext={(event) => ValidationUtil.stopEventPropagationIfValidationFails(event, this.formFields)}
                    onPrevious={() => this.formFields = []}>
                    <div style={{ margin: "20px 20px 20px 20px" }}>
                        <p><b>{this.getLocalizedText('Questions_And_Concerns')}</b><br />
                            {this.getLocalizedText('Questions_And_Concerns_Section1_Description')}
                        </p>

                        <p>
                            <ul className={"list-unstyled"}>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>a.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section1_Point1')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>b.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section1_Point2')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>b.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section1_Point3')}
                                    </p>
                                </li>
                            </ul>
                        </p>

                        <p>
                            {this.getLocalizedText('Questions_And_Concerns_Section2_Description')}
                            <ul className={"list-unstyled"}>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>a.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section2_Point1')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>b.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section2_Point2')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>c.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section2_Point3')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>d.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section2_Point4')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>e.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section2_Point5')}
                                    </p>
                                </li>
                            </ul>
                        </p>
                        <p>
                            {this.getLocalizedText('Questions_And_Concerns_Section3_Description')}
                            <ul className={"list-unstyled"}>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>a.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section3_Point1')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>b.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section3_Point2')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>c.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section3_Point3')}
                                    </p>
                                </li>
                                <li>
                                    <p><span className={"mr-2 d-inline font-weight-bold"}>d.</span>
                                        {this.getLocalizedText('Questions_And_Concerns_Section3_Point4')}
                                    </p>
                                </li>
                            </ul>
                        </p>
                    </div>
                </Wizard.Page>

                <Wizard.Page
                    onNext={(event) => ValidationUtil.stopEventPropagationIfValidationFails(event, this.formFields)}
                    onPrevious={() => this.formFields = []}>
                    <div style={{ margin: "20px 20px 20px 20px" }}>
                        <div style={{ border: "1px solid #000000", margin: "10px 0px", display: "flex" }}>
                            <div style={{ flex: 1, margin: "10px 15px" }}>
                                <p><b>{this.getLocalizedText('Final_Consent_Statement')}</b></p>
                            </div>
                            <div style={{ borderLeft: "1px solid #000000", padding: "10px" }}>
                                <FormField ref={formField => this.formFields.push(formField)} required
                                    validateOn={form.participateInTeleconsultation}>
                                    <p className="display-flex">
                                        <YesNoRadioInput id="participateInTeleconsultation"
                                                         value={form.participateInTeleconsultation}
                                                         enumLabel={enumLabel}
                                                         onValueChange={(val) => this.fieldChanged('participateInTeleconsultation', val)}/>
                                    </p>
                                </FormField>
                            </div>
                        </div>

                        <div className="container">
                            <div className="row">
                                <div className="col-md-3 dim14-form-label">
                                    {this.getLocalizedText('Date')}
                                </div>
                                <div className="col-md-3 dim14-base-input">
                                    {": " + moment(new Date(form.patientDate)).format('MMMM DD, YYYY')}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-3 dim14-form-label">
                                    {this.getLocalizedText('Patient_Name')}
                                </div>
                                <div className="col-md-3 dim14-base-input">
                                    {": " + patientName}
                                </div>
                                <div className="col-md-3 dim14-form-label">
                                    {this.getLocalizedText('Provider_Name')}
                                </div>
                                <div className="col-md-3 dim14-base-input">
                                    <TextInput value={form.providerName} disabled />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-3 dim14-form-label">
                                    {this.getLocalizedText('Print_Name')}
                                </div>
                                <div className="col-md-3 dim14-base-input">
                                    <FormField ref={formField => this.formFields.push(formField)}
                                        disabled={!isPatientMinor}
                                        required requiredMessage={this.getLocalizedText('Field_Required')}
                                        validateOn={form.guardianName}>
                                        <TextInput value={form.guardianName} alphabeticOnly
                                            onChange={(value) => this.fieldChanged('guardianName', value)} />
                                    </FormField>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-3 dim14-form-label">
                                    {this.getLocalizedText('Relationship_To_Patient')}
                                </div>
                                <div className="col-md-3 dim14-base-input">
                                    <FormField ref={formField => this.formFields.push(formField)}
                                        disabled={!isPatientMinor}
                                        required requiredMessage={this.getLocalizedText('Field_Required')}
                                        validateOn={form.guardianRelationship}>
                                        <TextInput value={form.guardianRelationship} alphabeticOnly
                                            onChange={(value) => this.fieldChanged('guardianRelationship', value)} />
                                    </FormField>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 dim14-form-label">
                                    {this.getLocalizedText('Patient_Signature')}
                                </div>
                                <div className="col-md-6 dim14-form-label">
                                    {this.getLocalizedText('Provider_Signature')}
                                </div>
                            </div>
                        </div>

                        <div className="container">
                            <div className="row">
                                <div className="col-md-6">
                                    <FormField ref={formField => this.formFields.push(formField)}
                                        required requiredMessage={this.getLocalizedText('Signature_Required')}
                                        validateOn={form.patientSignature}>
                                        <SignatureInput signature={form.patientSignature}
                                            onClear={() => {
                                                if (form) {
                                                    this.fieldChanged('patientSignature', null)
                                                }
                                            }}
                                            onSave={(signature) => {
                                                if (form) {
                                                    this.fieldChanged('patientSignature', signature)
                                                }
                                            }} />
                                    </FormField>
                                </div>
                                <div className="col-md-6">
                                    <FormField ref={formField => this.formFields.push(formField)}
                                        validateOn={form.providerSignature}
                                        required requiredMessage="Provider Signature required">
                                        <SignatureDisplay signature={form.providerSignature} />
                                    </FormField>
                                </div>
                            </div>
                        </div>
                    </div>
                </Wizard.Page>

                <Wizard.Page onPrevious={() => this.formFields = []}>
                    <div style={{ margin: "20px 20px 20px 20px" }}>
                        <div style={{ border: "1px solid", borderRadius: "5px", padding: "30px" }}>
                            <div className="container">
                                <div className="row">
                                    <div className="col-md-12">
                                        <p className="font-weight-bold">
                                            <u>{this.getLocalizedText('Official_Use_Only')}</u></p>
                                        <p className="font-weight-bold">
                                            {this.getLocalizedText('Witness_Statement_Header')}</p>
                                        <p><i>{this.getLocalizedText('Witness_Statement')}</i></p>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-3 dim14-form-label">
                                        {this.getLocalizedText('Witness_Name')}
                                    </div>
                                    <div className="col-md-9 dim14-base-input">
                                        <TextInput value={form.witnessName} alphabeticOnly
                                            onChange={(value) => this.fieldChanged('witnessName', value)} />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="col-md-6 dim14-form-label">
                                            {this.getLocalizedText('Witness_Signature')}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <SignatureInput signature={form.witnessSignature}
                                            onClear={() => {
                                                this.fieldChanged('witnessSignature', null)
                                            }}
                                            onSave={(signature) => {
                                                this.fieldChanged('witnessSignature', signature)
                                            }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Wizard.Page>
            </Wizard>
        );
    }
}